import { FeathersService } from "@feathersjs/feathers";
import { Application } from "@feathersjs/feathers/lib/declarations";
import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import { DataType, IData, Services } from "types";
import client from "../feathers";

export abstract class Data<I extends IData> implements IData {
  /**
   * Note: Mobx requires default values to observe members
   */
  id?:         number = null;
  created_at?: Date   = null;
  updated_at?: Date   = null;

  protected service: FeathersService<Application<I>, any>; // TODO figure out proper type here

  protected constructor(public readonly data_type: DataType, data?: I) {
    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually.
    //
    // Members of derived classes have to be added manually in the same way!
    makeObservable(this, {
      id:         observable,
      created_at: observable,
      updated_at: observable,

      canEdit: computed,
      isNew:   computed,

      save:    action,
      remove:  action,
      archive: action,
      set:     action,
      patch:   action
    });
    // Get a reference to the feathers service for this type
    this.service = client.service(data_type as unknown as Services) as FeathersService<I>;

    // Initialize data
    this.fromData(data);
  }

  /***********************************************
   * Database access
   ************************************************/

  /**
   * Reloads the data of the current object. Returns <true> if 
   * the refresh was successful, <false> otherwise. 
   */
  public refresh = async (): Promise<boolean> => {
    if(this.isNew) return false;
    try {
      this.patch((await this.service.get(this.id)) as I, false);
    } catch (err) {
      console.error("Failed to refresh data", this.id, this.data_type);
      return false;
    }
    return true;
  }

  /***********************************************
   * Virtual Functions. They provide base functionality.
   *
   * Overwrite these in derived classes!
   ************************************************/

  /**
   * Loads the row with @id from the database and returns a Data instance, 
   * created from the loaded data. 
   * If no @id is provided an empty find is used to load all available data.
   * Note, that the backend will ensure user access.
   */
  public static get = async (id?: number): Promise<Data<IData> | Data<IData>[]> => {
    return {} as Data<IData>;
  }

  /**
   * Abstract Method! This is not supposed to be called. But since typescript does not
   * support abstract, static methods this is a workaround.
   *
   * Factory method to create a Data object either from data or with default values.
   *
   * Note: created_at and updated_at are set by the backend
   * and, thus, do not have to be added here.
   */
  public static create(data: IData): Data<IData> {
    return {} as Data<IData>;
  }

  /**
   * Dates and IDs are stored as strings and have to be converted to Date Objects and numbers
   * respectively.
   */
  protected fromData(data: I): void {
    this.id         = data.id && Number(data.id);
    this.created_at = data.created_at && new Date(data.created_at);
    this.updated_at = data.updated_at && new Date(data.updated_at);
  }

  /**
   * Convert dates back to ISOStrings.
   */
  public toData(): any {
    return {
      id:         this.id,
      created_at: this.created_at && this.created_at.toISOString(),
      updated_at: this.updated_at && this.updated_at.toISOString(),
    };
  }

  /**
   * Can be used to patch a row rather than update it.
   * 
   * Note: Created at and updated at are listed as examples here, the provided values will be ignored by 
   * the backend and instead updated by the backend diretly. 
   */
  protected toPatchData(patch: Partial<I>): any {
    return {
      created_at: "created_at" in patch ? patch.created_at && patch.created_at.toISOString() : undefined,
      updated_at: "updated_at" in patch ? patch.updated_at && patch.updated_at.toISOString() : undefined,
    };
  }

  /**
   * @return <true> if the currently logged in user has the rights to edit this item.
   * 
   * Note: This is a helper for the frontend. The backend will prevent any unauthorized access.
   */
  public get canEdit(): boolean {
    return true;
  }

  
  /**
   * Archives the object. Work in progress...
   */
  public archive = async (): Promise<void> => {
    console.error("Data cannot be archived");
  };


  /***********************************************
   * The following functions are generic and do not need to be overwritten.
   ************************************************/
  /**
   * @return <true> if the object is new. An item is locally created without ID. This is set and
   *          created when it is saved the first time.
   */
  public get isNew(): boolean {
    return !this.id;
  }

  /**
   * Saves the object to the database. Updates the current object with the returned 
   * data if applicable, e.g. @updated_at. 
   *
   * @param patch If provided, PATCH is used rather than UPDATE to store the data
   * @return Data returned by the called service (i.e. patch, update or created)
   */
  public async save(patch?: Partial<I>): Promise<I> {
    try {
      // If the object already exists in the database, patch or update it
      let newData: I;
      if (this.id) {
        if (patch)
          newData = (await this.service.patch(this.id, this.toPatchData(patch))) as I;
        else
          newData = (await this.service.update(this.id, this.toData())) as I;
      } else {
        // If the objet does not exist, create it
        newData = (await this.service.create({...this.toData(), id: undefined })) as I;
      }
      // Add the new @updated_at and possible @created_at and @id to the object. 
      // runInAction is need as this is an async call.
      if(!newData || !Object.keys(newData).length)  throw new Error("Service call returned empty");
      runInAction(() => this.fromData(newData)); 
      return newData;
    } catch (error) {
      console.error("Failed to save object", this, error);
      return null;
    }
  }

  /**
   * Removes the data object from the database
   */
  public remove = async (): Promise<boolean> => {
    try {
      await this.service.remove(this.id);
      return true;
    } catch (error) {
      console.error("Failed to remove object", error);
      return false;
    }
  };

  /**
   * Change property value
   *
   * @param prop  Property name to be set
   * @param value Value that @prop will be set to
   * @param save  If <true> value change will be patched to database
   * 
   * Note: This does not(!) take care of number/string/date conversions like
   * @from data and @patch do! 
   */
  public async set(prop: keyof this, value: any, save = false): Promise<boolean> {
    // if (save && !this.isValueChanged(prop, value) && !this.isNew) return true;
    this[prop] = value;
    try {
      if (save) return Boolean(await this.save({ [prop]: value } as I));
    } catch (err) {
      console.error("Failed to set object value", this, err);
      return false;
    }
    return true;
  }

  /**
   * Change property values. Similar to @set just with several values.
   *
   * @param data  Data that should replace the current data
   * @param save  If <true> value change will be patched to database
   */
  public async patch(data: Partial<I>, save = false): Promise<boolean> {
    this.fromData(Object.assign(this.toData(), data));
    try {
      if (save) await this.save(data);
    } catch (err) {
      console.error("Failed to patch object values", this, err);
      return false;
    }
    return true;
  }


  // Utility function to deep compare objects
  private compare(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) !== JSON.stringify(obj2);

    // Convert MobX observables to plain JS objects for accurate comparison
    obj1 = toJS(obj1);
    obj2 = toJS(obj2);

    // Quick equality check for primitives and references
    if (obj1 === obj2) return true;

    // Further checks for null or different types
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
        return false;
    }

    // Handling arrays and object properties
    let keysA = Object.keys(obj1), keysB = Object.keys(obj2);
    if (keysA.length !== keysB.length) return false;

    for (let key of keysA) {
        if (!keysB.includes(key)) return false;

        // Recursive check for nested objects or arrays
        if (typeof obj1[key] === 'object' || typeof obj2[key] === 'object') {
            if (!this.compare(obj1[key], obj2[key])) return false;
        } else {
            // Direct comparison for primitives
            if (obj1[key] !== obj2[key]) return false;
        }
    }
    return true;
}

  // Check if the property's new value is different from the old value
  private isValueChanged(prop: keyof this, value: any): boolean {
      if (this.isNew) return false; // Always false for new instances
      return !this.compare(value, this[prop as keyof this]);
  }
}
